import { z } from 'zod';
import { emailSchema, httpsUrlSchema, tenantCodeSchema } from './common';
import { tenantStatusSchema } from './directory';
import { schoolMiddlewaresStatusSchema } from './middleware-allow-list';
import { reportedVersionLabelSchema } from './school-version';
import { semverSchema } from './version';

export const exposureModeSchema = z.enum(['passthrough', 'cloudflare-tunnel']);
export type ExposureMode = z.infer<typeof exposureModeSchema>;

const hostnameSchema = z
  .string()
  .toLowerCase()
  .regex(/^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, 'Invalid hostname');

/** Longest school short name (tenant_config.short_name, manifest, Admin → Branding). */
export const SHORT_NAME_MAX = 24;

/** Short name a school starts with: the first 24 characters of its name, trimmed. */
export function defaultShortName(name: string): string {
  return name.trim().slice(0, SHORT_NAME_MAX).trim();
}

/**
 * School short name as the Host Admin enters it (app header, home screen, emails): trimmed,
 * 1-24 characters, no control characters or angle brackets (it lands in email subjects and HTML).
 */
export const shortNameSchema = z
  .string()
  .trim()
  .min(1, 'Enter a short name')
  .max(SHORT_NAME_MAX, `At most ${SHORT_NAME_MAX} characters`)
  .refine((v) => !/[\p{Cc}<>]/u.test(v), 'No control characters or < >');

/** Host Admin tenant record (brief §10). Subscription fields only; no billing. */
export const tenantRecordSchema = z.object({
  code: tenantCodeSchema,
  name: z.string().min(1).max(120),
  /** Additive (2026-10-10): entered on create; older records get defaultShortName(name). */
  shortName: z.string().min(1).max(SHORT_NAME_MAX),
  codeHostname: hostnameSchema,
  customDomain: hostnameSchema.nullable(),
  /** Canonical = custom domain if set, else code hostname. Used as tenantUrl and Keycloak issuer base. */
  tenantUrl: httpsUrlSchema,
  exposureMode: exposureModeSchema,
  tailscaleAddress: z.string().nullable(),
  status: tenantStatusSchema,
  subscription: z.object({
    plan: z.string().max(60).nullable(),
    startsOn: z.iso.date().nullable(),
    endsOn: z.iso.date().nullable(),
    contactName: z.string().max(120).nullable(),
    contactEmail: emailSchema.nullable(),
  }),
  enrolledAt: z.iso.datetime().nullable(),
});
export type TenantRecord = z.infer<typeof tenantRecordSchema>;

/**
 * POST central /v1/tenants/enroll — Tailscale only, one-time token (brief §8.3).
 * `tenantCode` is optional since 2026-10-09 (token-only install): central finds the tenant from
 * the token hash. A school that still sends it gets a 401 unless it matches the token's tenant.
 */
export const enrollRequestSchema = z.object({
  enrollmentToken: z.string().min(32).max(256),
  tenantCode: tenantCodeSchema.optional(),
  version: semverSchema,
});

/**
 * The school's identity as the Host Admin entered it (central is its only source). The school
 * seeds tenant_config from it and makes `bootstrapAdminEmail` its first Tenant Admin. Central
 * deletes the email in the same transaction that consumes the token, so it is null on any later
 * enrollment unless the Host Admin enters one again.
 */
export const enrolledTenantSchema = z.object({
  code: tenantCodeSchema,
  name: z.string().min(1).max(120),
  shortName: z.string().min(1).max(SHORT_NAME_MAX),
  codeHostname: hostnameSchema,
  customDomain: hostnameSchema.nullable(),
  /** Custom domain if set, else the code hostname (Keycloak issuer host). */
  canonicalHost: hostnameSchema,
  tenantUrl: httpsUrlSchema,
  bootstrapAdminEmail: emailSchema.nullable(),
});
export type EnrolledTenant = z.infer<typeof enrolledTenantSchema>;

export const enrollResponseSchema = z.object({
  pushRelayApiKey: z.string().min(32),
  privateAdminCredential: z.string().min(32),
  centralApiVersion: z.literal('1'),
  /** Additive (apiVersion 1): absent from centrals older than 2026-10-09. */
  tenant: enrolledTenantSchema.optional(),
});
export type EnrollRequest = z.infer<typeof enrollRequestSchema>;
export type EnrollResponse = z.infer<typeof enrollResponseSchema>;

/** GET tenant /private/v1/status (brief §8.2). */
export const privateStatusSchema = z.object({
  version: semverSchema,
  uptimeSeconds: z.number().int().min(0),
  database: z.enum(['ok', 'down']),
  keycloak: z.enum(['ok', 'down']),
  userCount: z.number().int().min(0),
  /** Additive (school version visibility): Keycloak image tag, null if unknown. */
  keycloakVersion: reportedVersionLabelSchema,
  /** Additive: TP_RELEASE_VERSION from the school's .env, null if unset. */
  releaseVersion: reportedVersionLabelSchema,
  /** Additive (Phase 2 plug-ins): allowed middlewares and configured yes/no; absent on older schools. */
  middlewares: schoolMiddlewaresStatusSchema.optional(),
});
export type PrivateStatus = z.infer<typeof privateStatusSchema>;

/** Gateway routing entry rendered by central-api into the SNI gateway config. */
export const gatewayRouteSchema = z.object({
  hostname: hostnameSchema,
  tenantCode: tenantCodeSchema,
  backend: z.string().min(1),
  exposureMode: exposureModeSchema,
});
export type GatewayRoute = z.infer<typeof gatewayRouteSchema>;

/**
 * Derives the gateway table: one route per hostname (code + custom), passthrough tenants only.
 * Suspended tenants keep their route; the school itself returns the suspended state.
 */
export function gatewayRoutesFor(tenants: readonly TenantRecord[]): GatewayRoute[] {
  return tenants
    .filter(
      (t) => t.exposureMode === 'passthrough' && t.status !== 'disabled' && t.tailscaleAddress,
    )
    .flatMap((t) =>
      [t.codeHostname, t.customDomain]
        .filter((h): h is string => h !== null)
        .map((hostname) => ({
          hostname,
          tenantCode: t.code,
          backend: `${t.tailscaleAddress}:443`,
          exposureMode: t.exposureMode,
        })),
    );
}

/** PUT tenant /private/v1/tenant-status — central tells the school its subscription status. */
export const tenantStatusUpdateSchema = z.object({
  status: tenantStatusSchema.exclude(['disabled']),
});
export type TenantStatusUpdate = z.infer<typeof tenantStatusUpdateSchema>;
